import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "../lib/supabase";
import { env } from "../lib/env";

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isPlatformMember: boolean;
  displayName: string;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const demoUser = {
  id: "00000000-0000-4000-8000-000000000999",
  aud: "authenticated",
  role: "authenticated",
  email: "demo@yatta.com.br",
  app_metadata: {},
  user_metadata: { full_name: "Administrador Demo" },
  created_at: new Date(0).toISOString(),
} as User;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(env.useMocks ? demoUser : null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(!env.useMocks);
  const [isPlatformMember, setIsPlatformMember] = useState(env.useMocks);

  useEffect(() => {
    if (env.useMocks) return;

    void supabase.auth.getSession().then(({ data, error }) => {
      if (error) console.error(error);
      setSession(data.session);
      setUser(data.session?.user ?? null);
      setLoading(false);
    });

    const { data: listener } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
      setUser(nextSession?.user ?? null);
    });

    return () => listener.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (env.useMocks || !user) {
      setIsPlatformMember(env.useMocks);
      return;
    }
    void supabase
      .from("platform_members")
      .select("profile_id")
      .eq("profile_id", user.id)
      .eq("status", "ACTIVE")
      .maybeSingle()
      .then(({ data, error }) => {
        if (error) console.warn("platform member lookup:", error.message);
        setIsPlatformMember(Boolean(data));
      });
  }, [user]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      session,
      loading,
      isPlatformMember,
      displayName:
        (user?.user_metadata?.full_name as string | undefined) ?? user?.email ?? "Usuário",
      signIn: async (email, password) => {
        if (env.useMocks) {
          setUser({ ...demoUser, email, user_metadata: { full_name: "Administrador Demo" } });
          return;
        }
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
      },
      signOut: async () => {
        if (env.useMocks) {
          setUser(null);
          return;
        }
        const { error } = await supabase.auth.signOut();
        if (error) throw error;
      },
    }),
    [user, session, loading, isPlatformMember],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside AuthProvider.");
  return value;
}
